import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { createLogger } from '@hunting-nature/shared-core/logging';
import { Store } from '@ngrx/store';
import { catchError, concatMap, from, map, of, switchMap, tap, withLatestFrom } from 'rxjs';

import { ImageApiService } from '../../../pages/gallery/image-gallery/services/image-api.service';
import { ImageProcessingService } from '../../../pages/gallery/image-gallery/services/image-processing.service';
import { ImageService } from '../../../pages/gallery/image-gallery/services/image.service';
import type { StorageInformation } from '../../../pages/menu/device-settings/storage-management/components/storage-information/storage-information';
import { StorageInformationService } from '../../../pages/menu/device-settings/storage-management/components/storage-information/storage-information.service';
import { BrowserService } from '../../services/browser.service';
import { SettingsService } from '../../services/settings.service';
import { GeneralActions } from '../general/general.actions';
import { GeneralSelectors } from '../general/general.reducer';

import { SettingsActions } from './settings.actions';

@Injectable()
export class SettingsEffects {
  // services
  private readonly actions$ = inject(Actions);
  private readonly storageInformationService = inject(StorageInformationService);
  private readonly cacheService = inject(ImageProcessingService);
  private readonly imageService = inject(ImageService);
  private readonly imageApiService = inject(ImageApiService);
  private readonly settingsService = inject(SettingsService);
  private readonly store = inject(Store);
  private readonly browserService = inject(BrowserService);
  private readonly log = createLogger('SettingsEffects');

  initStorageInformation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SettingsActions.initStorageInformation),
        switchMap(() =>
          this.storageInformationService.getStorageInformation$().pipe(
            map((storageInformation: StorageInformation) =>
              SettingsActions.initStorageInformationSucceeded({
                storageInformation,
              })
            ),
            catchError((error: unknown) => {
              this.log.error('getStorageInformation$ failed', { error });
              return of(SettingsActions.initStorageInformationFailed({ error }));
            })
          )
        )
      ),
    { dispatch: true }
  );

  deleteFullAppCache$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SettingsActions.deleteFullAppCache),
        switchMap(() =>
          from(this.cacheService.deleteAll$()).pipe(
            tap(() => this.imageApiService.clearDetailCache()),
            concatMap(() => of(SettingsActions.deleteFullAppCacheSucceeded(), SettingsActions.initStorageInformation())),
            catchError((error: unknown) => {
              this.log.error('deleteFullAppCache failed', { error });
              return of(SettingsActions.deleteFullAppCacheFailed({ error }));
            })
          )
        )
      ),
    { dispatch: true }
  );

  deleteUserSettings$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SettingsActions.deleteUserSettings),
        switchMap(() => this.settingsService.clear$())
      ),
    { dispatch: false }
  );

  resetApplication$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SettingsActions.resetApplication),
        concatMap(() => of(SettingsActions.deleteFullAppCache(), SettingsActions.deleteUserSettings(), GeneralActions.logout()))
      ),
    { dispatch: true }
  );

  showLegalDocument$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SettingsActions.showTermsAndConditions),
        withLatestFrom(this.store.select(GeneralSelectors.activeAppLanguage)),
        switchMap(([, lang]) => this.settingsService.fetchLegalDocuments$(lang).pipe(map(([document]) => document.documentUrl))),
        concatMap(pdf => of(SettingsActions.fetchConsentSucceeded({ pdf }))),
        catchError(() => of(SettingsActions.fetchConsentFailed()))
      ),
    { dispatch: true }
  );

  openPdfDocument$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SettingsActions.fetchConsentSucceeded),
        switchMap(action => this.browserService.openExternalUrl(action.pdf))
      ),
    { dispatch: false }
  );
}
